/**
 * MLMBAZAAR hero — wave particle field
 * A dense plane of particles displaced by a stack of Gerstner waves (rolling,
 * folding crests) plus a slow simplex-noise swell (fluid turbulence).
 * Rendered as an additive point cloud on the GPU — continuous, subtle motion.
 */
(function () {
const THREE = window.THREE;

const container = document.getElementById('hero-particles');

if (container) {
  main();
}

function main() {
const isMobile = window.innerWidth < 768;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const NOISE = `
vec3 mod289(vec3 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x){ return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}
`;

const VERTEX = `
uniform float uTime;
uniform float uPixelRatio;

attribute float aSize;

varying float vHeight;
varying float vSlope;
varying float vFade;

${NOISE}

vec3 gerstner(vec2 p, vec2 dir, float k, float w, float A, float phase, out float c) {
  float f = k * dot(dir, p) - w * uTime + phase;
  c = cos(f);
  return vec3(-dir.x * A * sin(f), A * c, -dir.y * A * sin(f));
}

void main() {
  vec3 pos = position;

  float c1, c2, c3, c4, c5;
  pos += gerstner(pos.xz, normalize(vec2(1.00, 0.35)), 0.34, 0.75, 0.55, 1.3, c1);
  pos += gerstner(pos.xz, normalize(vec2(-0.55, 0.85)), 0.50, 1.00, 0.35, 2.1, c2);
  pos += gerstner(pos.xz, normalize(vec2(0.25, -1.00)), 0.26, 0.55, 0.50, 0.4, c3);
  pos += gerstner(pos.xz, normalize(vec2(0.95, 0.50)), 0.72, 1.45, 0.22, 3.3, c4);
  pos += gerstner(pos.xz, normalize(vec2(-0.80, -0.45)), 0.42, 0.90, 0.30, 5.0, c5);

  // Slow fluid swell — two octaves of simplex noise advecting across the field
  vec2 nuv = pos.xz * 0.075 + vec2(uTime * 0.035, uTime * 0.028);
  float n1 = snoise(vec3(nuv, uTime * 0.16));
  float n2 = snoise(vec3(nuv * 2.3, uTime * 0.22));
  pos.y += n1 * 1.1 + n2 * 0.45;

  const float AMP = 1.92;    // sum of Gerstner amplitudes
  const float SWELL = 1.55;  // noise displacement range
  const float TOT = AMP + SWELL;

  vHeight = clamp((pos.y + TOT) / (2.0 * TOT), 0.0, 1.0);
  vSlope = (c1 * 0.55 + c2 * 0.35 + c3 * 0.50 + c4 * 0.22 + c5 * 0.30) / AMP;

  // Fade into the background towards the far edge of the field
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  float dist = -mv.z;
  vFade = 1.0 - smoothstep(30.0, 78.0, dist);

  gl_Position = projectionMatrix * mv;
  gl_PointSize = uPixelRatio * aSize * (240.0 / dist) * (0.85 + 0.3 * vHeight);
}
`;

const FRAGMENT = `
varying float vHeight;
varying float vSlope;
varying float vFade;

void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float d = length(uv);
  if (d > 0.5) discard;

  // Soft blurry falloff from center of each sprite
  float edge = 1.0 - smoothstep(0.0, 0.5, d);
  edge = edge * edge;

  vec3 deep  = vec3(0.22, 0.15, 0.80);   // indigo trough
  vec3 mid   = vec3(0.72, 0.72, 1.00);   // lavender
  vec3 crest = vec3(0.95, 0.96, 1.00);   // near-white peaks

  vec3 col = mix(deep, mid, smoothstep(0.05, 0.50, vHeight));
  col = mix(col, crest, smoothstep(0.50, 0.95, vHeight));

  float alpha = (0.08 + 0.50 * vHeight + 0.18 * vSlope) * edge * vFade;
  gl_FragColor = vec4(col, alpha);
}
`;

let renderer, scene, camera, points, material;
let W = 0, H = 0;
let time = 0;
let inView = true;
const clock = new THREE.Clock();

init();

// Don't draw the field while it's off-screen: a continuous render loop
// would otherwise compete with Lenis' smooth-scroll rAF for the whole page.
const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => { inView = entry.isIntersecting; });
}, { threshold: 0.01 });
visibilityObserver.observe(container);

function init() {
  renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: false,
    powerPreference: 'high-performance',
  });
  renderer.setClearColor(0x000000, 0);
  container.appendChild(renderer.domElement);

  scene = new THREE.Scene();

  camera = new THREE.PerspectiveCamera(45, 1, 0.1, 500);
  camera.position.set(0, 30, 55);
  camera.lookAt(0, 0, 0);

  buildWaveField();
  resize();

  window.addEventListener('resize', resize);
  animate();
}

function buildWaveField() {
  const segments = isMobile ? 140 : 200;
  const size = 76;

  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
  geometry.rotateX(-Math.PI / 2);

  const count = geometry.attributes.position.count;
  const sizes = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    sizes[i] = 1.0 + Math.random() * 0.9;
  }
  geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));

  material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uPixelRatio: { value: 1 },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  scene.add(points);
}

function resize() {
  W = container.clientWidth || window.innerWidth;
  H = container.clientHeight || window.innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  renderer.setPixelRatio(dpr);
  renderer.setSize(W, H, false);
  material.uniforms.uPixelRatio.value = dpr;
  camera.aspect = W / H;
  camera.updateProjectionMatrix();
}

function animate() {
  requestAnimationFrame(animate);

  const dt = Math.min(clock.getDelta(), 0.05);
  time += dt * (reducedMotion ? 0.12 : 1);

  material.uniforms.uTime.value = time;

  if (!inView) return;

  // Slow breathing camera drift
  const sway = time * 0.045;
  camera.position.x = Math.sin(sway) * 3;
  camera.position.y = 30 + Math.cos(sway * 0.72) * 1.8;
  camera.position.z = 55 + Math.sin(sway * 0.5) * 2.2;
  camera.lookAt(0, 0, 0);

  // The wave field itself drifts slowly, like a surface turning
  points.rotation.y = Math.sin(time * 0.04) * 0.2;

  renderer.render(scene, camera);
}
}
})();
